import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CheckoutStatus() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | success | expired | error
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("paypal") === "success") {
      setState("success");
      clear();
      return;
    }
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 30) { setState("pending"); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" || data.payment_status === "no_payment_required") {
          setAmount((data.amount_total / 100).toFixed(2));
          setState("success");
          clear();
          return;
        }
        if (data.status === "expired") { setState("expired"); return; }
        setTimeout(poll, attempts < 10 ? 2000 : 4000);
      } catch {
        setTimeout(poll, 3000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[80vh] pt-24 flex items-center justify-center bg-background px-5">
      <div className="max-w-md w-full bg-white border border-brand/10 rounded-2xl p-10 text-center shadow-sm" data-testid="checkout-status">
        {state === "checking" && (
          <>
            <Loader2 className="h-14 w-14 text-brand-500 animate-spin mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2">Please wait a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="h-16 w-16 text-brand-500 mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6" data-testid="checkout-success">Payment successful!</h1>
            <p className="text-muted-foreground mt-2">
              Thank you for your purchase{amount ? ` of $${amount}` : ""}. A confirmation will
              be sent to you shortly.
            </p>
            <Button onClick={() => navigate("/shop")} className="mt-8 rounded-full bg-brand hover:bg-brand-700 text-white">
              <ShoppingBag className="h-4 w-4 mr-2" /> Continue Shopping
            </Button>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-14 w-14 text-brand-500 mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6" data-testid="checkout-pending">Payment is still processing</h1>
            <p className="text-muted-foreground mt-2">
              Your bank is taking longer than usual to confirm. Please don't pay again. We'll email you as soon as it's confirmed, or call +256 766 553 449.
            </p>
            <Button onClick={() => window.location.reload()} className="mt-8 rounded-full bg-brand hover:bg-brand-700 text-white" data-testid="checkout-recheck-btn">
              Check again
            </Button>
          </>
        )}
        {(state === "expired" || state === "error") && (
          <>
            <XCircle className="h-16 w-16 text-destructive mx-auto" />
            <h1 className="font-heading font-bold text-2xl text-brand mt-6">Payment not completed</h1>
            <p className="text-muted-foreground mt-2">
              Your payment session expired or could not be verified. Please try again.
            </p>
            <Button onClick={() => navigate("/shop")} className="mt-8 rounded-full bg-brand hover:bg-brand-700 text-white">
              Back to Shop
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
